import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@opendate-app/setlist';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import Countdown from 'react-countdown';
import { computeCheckoutCountdown } from '../../../shared/checkoutCountdown';
import { paymentElementOptions } from '../../../shared/stripePaymentElementOptions';
import ContactInfoForm from '../../shared/components/ContactInfoForm';
import ConfirmCheckoutHeader from '../../shared/components/ConfirmCheckoutHeader';
import ExistingMembershipDisplay from './ExistingMembershipDisplay';
import GiftRecipientForm from './GiftRecipientForm';
import GiftRenewalChoice from './GiftRenewalChoice';
import MemberContactFields, { contactComplete, contactPayload } from './MemberContactFields';
import { Alert } from '@opendate-app/setlist';
import { sharedAttributionVisitorId, useAttributionVisitor } from '../../../shared/attribution/useAttributionVisitor';

export const giftSummary = (gift) => [
  `Gift for ${`${gift.first_name} ${gift.last_name || ''}`.trim()}`,
  gift.anonymous && 'name private',
  gift.send_on && `email sends ${new Date(`${gift.send_on}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
].filter(Boolean).join(' · ');

function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const stripeAppearance = {
  theme: 'stripe',
  variables: {
    fontFamily: 'Open Sans, system-ui, sans-serif',
    fontSizeBase: "14px",
    borderRadius: '8px',
    colorDanger: "#FF4C00"
  },
  rules: {
    ".Input": {
      boxShadow: "none",
      border: "1px solid #e6e6e6"
    }
  }
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const countdownRenderer = ({ minutes, seconds }) => {
  const formattedSeconds = seconds.toString().padStart(2, '0');
  return <span>{minutes}:{formattedSeconds}</span>;
};

const gettingCloseToExpiration = (percentage) => percentage < 37.5;

export const CheckoutFormInner = ({
  membership,
  baseUrl,
  cancelUrl,
  submitLabel,
  onPaymentReady
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const completeUrl = `${baseUrl}/memberships/${membership.token}/complete`;

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: completeUrl
      },
      redirect: "if_required"
    });

    if (stripeError) {
      setIsSubmitting(false);
      setError(stripeError.message);
    } else {
      window.location.replace(completeUrl);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row" style={{ marginTop: "23px" }}>
        <div className="col-12">
          <p className="mb-0 mb-3" style={{ fontSize: "16px" }}>
            <strong>Payment method</strong>
          </p>
        </div>
        <div className="col-12">
          {error && (
            <div className="alert alert-danger border-0 text-white"
                 style={{ background: "#FF4C00" }}
                 role="alert">
              {error}
            </div>
          )}
          <PaymentElement options={paymentElementOptions()} onReady={onPaymentReady} />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px", marginBottom: "26px" }}>
        <div className="col-xs-auto" style={{ paddingLeft: "15px" }}>
          <i className="fas fa-exclamation-circle small" style={{ color: "#f8cb58" }}></i>
        </div>
        <div className="col">
          <p className="mb-0 small" style={{ color: "#686868" }}>
            By clicking "{submitLabel}," I confirm that I have read and accept the{' '}
            <a target="_blank" rel="noopener noreferrer" href="https://www.opendate.io/terms">Terms of Service</a>,{' '}
            <a target="_blank" rel="noopener noreferrer" href="https://www.opendate.io/privacy">Privacy Policy</a>{' '}
            and Refund Policy. I also agree that Opendate may share my information with the event organizer.
          </p>
        </div>
      </div>

      <div className="row" style={{ paddingTop: "5px" }}>
        <div className="col d-flex align-items-center">
          <p className="mb-0" style={{ fontSize: "14px", color: "#686868" }}>
            Powered by
            <a href="https://www.opendate.io/" target="_blank" rel="noopener noreferrer">
              <img src="/opendate_logo.png" className="ml-2" style={{ width: "88px" }} alt="Opendate" />
            </a>
          </p>
        </div>
        <div className="col text-right" style={{ alignItems: "center", display: "flex", justifyContent: "flex-end" }}>
          <CancelLink cancelUrl={cancelUrl} disabled={isSubmitting} />
          <Button variant="primary" type="submit" disabled={!stripe || isSubmitting}>
            {isSubmitting ? 'Placing Order...' : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

CheckoutFormInner.propTypes = {
  membership: PropTypes.object.isRequired,
  baseUrl: PropTypes.string.isRequired,
  cancelUrl: PropTypes.string,
  submitLabel: PropTypes.string,
  onPaymentReady: PropTypes.func
};

CheckoutFormInner.defaultProps = {
  cancelUrl: null,
  submitLabel: 'Place Order',
  onPaymentReady: undefined
};

const CancelLink = ({ cancelUrl, disabled }) => {
  if (!cancelUrl) return null;

  const baseStyle = { marginRight: "30px" };
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        style={{ ...baseStyle, color: "rgba(25, 130, 196, 0.25)", cursor: "not-allowed" }}
      >
        Cancel
      </span>
    );
  }
  return (
    <a
      href={cancelUrl}
      style={{ ...baseStyle, color: "#1982C4" }}
    >
      Cancel
    </a>
  );
};

CancelLink.propTypes = {
  cancelUrl: PropTypes.string,
  disabled: PropTypes.bool
};

const EventHeader = ({ confirm }) => {
  if (!confirm) return null;

  return (
    <div className="row" style={{ marginBottom: "24px" }}>
      <div className="col-12">
        <ConfirmCheckoutHeader confirm={confirm} />
      </div>
    </div>
  );
};

EventHeader.propTypes = {
  confirm: PropTypes.object
};

const MembershipCheckoutWidget = ({
  csrfToken,
  membershipProduct,
  benefits,
  price,
  monthlyPrice = null,
  team,
  venueOwnership,
  stripePublishableKey,
  stripeConnectedAccountId = null,
  baseUrl,
  userContext = {},
  existingMembership = null,
  previewOnly = false,
  skipLanding = false,
  postUrl = null,
  cancelUrl = null,
  confirm = null,
  giftingEnabled = false
}) => {
  const isLoggedIn = !!userContext.access_token;
  const notGiftable = giftingEnabled && !membershipProduct.giftable;
  const hasBothCadences = price && monthlyPrice;
  const [selectedCadence, setSelectedCadence] = useState(() => {
    if (price && monthlyPrice) return 'year';
    if (monthlyPrice) return 'month';
    return 'year';
  });
  const activePrice = selectedCadence === 'month' && monthlyPrice ? monthlyPrice : price;

  const attributionVisitorUuid = useAttributionVisitor('od_embed.js');
  const [step, setStep] = useState(skipLanding ? 'customer' : 'landing');
  const [customer, setCustomer] = useState({
    first_name: userContext.first_name || '',
    last_name: userContext.last_name || '',
    email: userContext.email || '',
    confirm_email: userContext.email || ''
  });
  const [gift, setGift] = useState({ enabled: false, name: '', email: '', anonymous: false, note: '', send_later: false, send_on: '' });
  const [contact, setContact] = useState({ address1: '', address2: '', city: '', address_state: '', zip: '', phone_number: '' });
  const [keepPaying, setKeepPaying] = useState(false);
  const contactSettings = gift.enabled ? {} : { collectAddress: membershipProduct.collect_address, collectPhone: membershipProduct.collect_phone };
  const [holdHeight, setHoldHeight] = useState(null);
  const customerStepRef = useRef(null);
  const [membership, setMembership] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [checkoutTimePercentageRemaining, setCheckoutTimePercentageRemaining] = useState(100);
  const [timeLimitReached, setTimeLimitReached] = useState(false);
  const [discoveredExistingMembership, setDiscoveredExistingMembership] = useState(null);
  const [existingMemberDismissed, setExistingMemberDismissed] = useState(false);

  // Anchor the countdown to the client's clock once per reservation so a skewed
  // browser clock can't make it expire instantly (O-2939).
  const countdownRef = useRef({ token: null, target: null, total: null });
  if (membership && countdownRef.current.token !== membership.token) {
    const { target, total } = computeCheckoutCountdown(membership.seconds_until_expiry);
    countdownRef.current = { token: membership.token, target, total };
  }

  useEffect(() => {
    if (stripePublishableKey) {
      const options = stripeConnectedAccountId
        ? { stripeAccount: stripeConnectedAccountId }
        : undefined;
      setStripePromise(loadStripe(stripePublishableKey, options));
    }
  }, [stripePublishableKey, stripeConnectedAccountId]);

  const subtotal = activePrice.amount_cents / 100;
  const absorb_fees = membershipProduct.absorb_fees;
  const totalFees = absorb_fees ? 0 : (activePrice.total_fee_amount_cents || 0) / 100;
  const totalTax = (activePrice.total_tax_amount_cents || 0) / 100;
  const total = activePrice.customer_pays_cents / 100;
  const offersKeepPaying = gift.enabled && !notGiftable && !!membershipProduct.gift_buyers_can_keep_paying;
  const buyerKeepsPaying = offersKeepPaying && keepPaying;
  const renewalPrice = formatter.format(total).replace(/\.00$/, '');

  const emailsMatch = !customer.confirm_email || customer.email === customer.confirm_email;
  const emailValid = !customer.email || validateEmail(customer.email);

  const canProceedToPayment = () => {
    return (
      customer.first_name.length > 0 &&
      customer.last_name.length > 0 &&
      customer.email.length > 0 &&
      validateEmail(customer.email) &&
      customer.email === customer.confirm_email &&
      contactComplete(contact, contactSettings) &&
      !(gift.enabled && notGiftable) &&
      (!gift.enabled || (gift.name.trim().length > 0 && validateEmail(gift.email) && (!gift.send_later || gift.send_on.length > 0))) &&
      !isLoading
    );
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setCustomer({ ...customer, [field]: value });
  };

  const handleContinueToPayment = async () => {
    if (!canProceedToPayment()) return;

    setIsLoading(true);
    setError(null);

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    try {
      const response = await axios.post(
        postUrl || `${baseUrl}/memberships`,
        {
          attribution_visitor_uuid: attributionVisitorUuid || sharedAttributionVisitorId() || '',
          membership: {
            first_name: customer.first_name,
            last_name: customer.last_name,
            email: customer.email,
            membership_product_price_id: activePrice.id,
            ...contactPayload(contact, contactSettings),
            ...(gift.enabled && {
              gift: { name: gift.name, email: gift.email, anonymous: gift.anonymous, note: gift.note, send_on: gift.send_later ? gift.send_on : null, ...(offersKeepPaying && { keep_paying: keepPaying }) }
            })
          }
        }
      );

      setMembership(response.data);
      setHoldHeight(customerStepRef.current ? customerStepRef.current.getBoundingClientRect().height : null);
      setStep('payment');
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.existing_membership) {
        setDiscoveredExistingMembership(err.response.data.existing_membership);
        setExistingMemberDismissed(false);
        return;
      }
      if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
        return;
      }
      setError(err.response?.data?.errors?.join(', ') || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const OrderSummary = ({ title = "Order Summary" }) => (
    <div>
      <p style={{ fontSize: "18px", marginTop: "20px", marginBottom: "15px" }}>
        <strong>{title}</strong>
      </p>
      <div className="row" style={{ marginBottom: "12px" }}>
        <div className="col">
          <p className="mb-0" style={{ fontSize: "16px" }}>
            1 x {membershipProduct.name}
          </p>
        </div>
        <div className="col-xs-auto text-right" style={{ paddingRight: "15px" }}>
          <p className="mb-0" style={{ fontSize: "16px" }}>
            {formatter.format(subtotal)}
          </p>
        </div>
      </div>
      {!absorb_fees && (
        <div className="row" style={{ marginBottom: "12px" }}>
          <div className="col">
            <p className="mb-0" style={{ fontSize: "16px" }}>
              Fees
            </p>
          </div>
          <div className="col-xs-auto text-right" style={{ paddingRight: "15px" }}>
            <p className="mb-0" style={{ fontSize: "16px" }}>
              {formatter.format(totalFees)}
            </p>
          </div>
        </div>
      )}
      {totalTax > 0 && (
        <div className="row" style={{ marginBottom: "12px" }}>
          <div className="col">
            <p className="mb-0" style={{ fontSize: "16px" }}>
              Tax
            </p>
          </div>
          <div className="col-xs-auto text-right" style={{ paddingRight: "15px" }}>
            <p className="mb-0" style={{ fontSize: "16px" }}>
              {formatter.format(totalTax)}
            </p>
          </div>
        </div>
      )}
      <hr style={{ borderTop: "1px solid #ececec" }} />
      <div className="row" style={{ marginBottom: "12px" }}>
        <div className="col">
          <p className="mb-0" style={{ fontSize: "16px" }}>
            <strong>Total</strong>
          </p>
        </div>
        <div className="col-xs-auto text-right" style={{ paddingRight: "15px" }}>
          <p className="mb-0" style={{ fontSize: "16px" }}>
            <strong>{formatter.format(total)}</strong>
          </p>
        </div>
      </div>
      {buyerKeepsPaying && (
        <p className="small" style={{ color: "#686868" }}>
          Then {renewalPrice}/{selectedCadence === 'month' ? 'month' : 'year'} on your card. Turn it off anytime from your account.
        </p>
      )}
    </div>
  );
  OrderSummary.propTypes = { title: PropTypes.string };

  const SecondColumn = ({ title }) => (
    <div className="h-100">
      {membershipProduct.image_url && (
        <img src={membershipProduct.image_url} className="img-fluid rounded" alt={membershipProduct.name} />
      )}
      <div className="d-none d-md-block sticky-top">
        <OrderSummary title={title} />
      </div>
    </div>
  );

  const existingMember = existingMemberDismissed ? null : existingMembership || discoveredExistingMembership;
  if (existingMember) {
    return (
      <div>
        <EventHeader confirm={confirm} />
        <ExistingMembershipDisplay
          membership={existingMember}
          membershipProduct={membershipProduct}
          venueOwnership={venueOwnership}
          team={team}
          onBuyAsGift={giftingEnabled && membershipProduct.giftable ? () => {
            setExistingMemberDismissed(true);
            setDiscoveredExistingMembership(null);
            setGift({ ...gift, enabled: true });
            setStep(skipLanding ? 'customer' : 'landing');
          } : null}
        />
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div>
        <EventHeader confirm={confirm} />
        {previewOnly && (
          <div style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "10px 16px",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#856404"
          }}>
            <i className="fas fa-eye mr-2"></i>
            This membership is unpublished. This is a preview only.
          </div>
        )}
        <div style={{ marginBottom: "24px" }}>
          <h1 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "12px", color: "#1a1a1a" }}>
            {membershipProduct.name}
          </h1>
          {membershipProduct.description && (
            <p style={{ fontSize: "14px", color: "#717680", lineHeight: "1.6", margin: 0 }}>
              {membershipProduct.description}
            </p>
          )}
        </div>

        <div className="row">
          <div className="col-12 col-md-7 col-lg-8 mb-4">
            <div style={{
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              padding: "20px",
              marginBottom: "20px"
            }}>
              <div style={{ marginBottom: benefits.length > 0 || hasBothCadences ? "16px" : "0" }}>
                <h4 style={{ fontSize: "16px", fontWeight: "bold", margin: 0, color: "#1a1a1a" }}>
                  {membershipProduct.name}
                </h4>
                <p style={{ fontSize: "14px", color: "#535862", margin: 0 }}>
                  {formatter.format(subtotal)}/{selectedCadence === "month" ? "month" : "year"}
                  {notGiftable && " · Not available as a gift"}
                </p>
              </div>

              {hasBothCadences && (
                <div className="d-flex mb-3" style={{ gap: "12px" }}>
                  <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="radio"
                      name="cadence"
                      value="year"
                      checked={selectedCadence === "year"}
                      onChange={() => setSelectedCadence("year")}
                      className="mr-1"
                    />
                    Annual ({formatter.format(price.amount_cents / 100)}/year)
                  </label>
                  <label className="d-flex align-items-center mb-0" style={{ cursor: "pointer", fontSize: "14px" }}>
                    <input
                      type="radio"
                      name="cadence"
                      value="month"
                      checked={selectedCadence === "month"}
                      onChange={() => setSelectedCadence("month")}
                      className="mr-1"
                    />
                    Monthly ({formatter.format(monthlyPrice.amount_cents / 100)}/month)
                  </label>
                </div>
              )}

              {benefits.length > 0 && (
                <ul style={{
                  margin: 0,
                  paddingLeft: "20px",
                  color: "#535862",
                  fontSize: "14px",
                  lineHeight: "1.8"
                }}>
                  {benefits.map((benefit, index) => (
                    <li key={benefit.id || index}>{benefit.description}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <p className="mb-0" style={{ fontSize: "14px", color: "#686868" }}>
                Powered by
                <a href="https://www.opendate.io/" target="_blank" rel="noopener noreferrer">
                  <img src="/opendate_logo.png" className="ml-2" style={{ width: "88px" }} alt="Opendate" />
                </a>
              </p>
              <Button variant="primary" onClick={() => setStep('customer')} disabled={previewOnly}>
                {previewOnly ? "Preview Only" : "Next"}
              </Button>
            </div>
          </div>

          <div className="col-12 col-md-5 col-lg-4 mb-4">
            {membershipProduct.image_url && (
              <div style={{ marginBottom: "24px" }}>
                <img
                  src={membershipProduct.image_url}
                  className="img-fluid rounded"
                  alt={membershipProduct.name}
                  style={{ width: "100%", borderRadius: "8px" }}
                />
              </div>
            )}

            <div>
              <p style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "16px", color: "#1a1a1a" }}>
                Order Summary
              </p>

              <div className="d-flex justify-content-between" style={{ marginBottom: "12px" }}>
                <span style={{ fontSize: "14px", color: "#535862" }}>1 x {membershipProduct.name}</span>
                <span style={{ fontSize: "14px", color: "#535862" }}>{formatter.format(subtotal)}</span>
              </div>

              {!absorb_fees && (
                <div className="d-flex justify-content-between" style={{ marginBottom: "12px" }}>
                  <span style={{ fontSize: "14px", color: "#535862" }}>Fees</span>
                  <span style={{ fontSize: "14px", color: "#535862" }}>{formatter.format(totalFees)}</span>
                </div>
              )}

              {totalTax > 0 && (
                <div className="d-flex justify-content-between" style={{ marginBottom: "12px" }}>
                  <span style={{ fontSize: "14px", color: "#535862" }}>Tax</span>
                  <span style={{ fontSize: "14px", color: "#535862" }}>{formatter.format(totalTax)}</span>
                </div>
              )}

              <hr style={{ borderTop: "1px solid #ececec", margin: "12px 0" }} />

              <div className="d-flex justify-content-between">
                <span style={{ fontSize: "14px", fontWeight: "bold", color: "#1a1a1a" }}>Total</span>
                <span style={{ fontSize: "14px", fontWeight: "bold", color: "#1a1a1a" }}>{formatter.format(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'customer') {
    return (
      <div ref={customerStepRef}>
        <EventHeader confirm={confirm} />
        <div className="row">
          <div className="col">
            <p className="mb-3 mt-1 text-dark" style={{ fontSize: "24px" }}>
              <strong>Checkout</strong>
            </p>
          </div>
        </div>
        <div className="row" style={{ marginTop: "13px" }}>
          <div className="col-12 col-md-6 col-lg-8 mb-3">
            <form onSubmit={(e) => { e.preventDefault(); handleContinueToPayment(); }}>
              <div className="row">
                <div className="col-12">
                  <p style={{ fontSize: "16px", marginBottom: "12px" }}>
                    <strong>Contact Info</strong>
                  </p>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger border-0 text-white"
                     style={{ background: "#FF4C00" }}
                     role="alert">
                  {error}
                </div>
              )}

              <ContactInfoForm
                firstName={customer.first_name}
                lastName={customer.last_name}
                email={customer.email}
                confirmEmail={customer.confirm_email}
                onFirstNameChange={handleChange('first_name')}
                onLastNameChange={handleChange('last_name')}
                onEmailChange={handleChange('email')}
                onConfirmEmailChange={handleChange('confirm_email')}
                disabled={isLoggedIn}
                emailError={customer.email && !emailValid}
                confirmEmailError={!emailsMatch}
              />

              <MemberContactFields contact={contact} onChange={setContact} {...contactSettings} />

              {giftingEnabled && (
                <GiftRecipientForm
                  gift={gift}
                  onChange={(next) => { setError(null); setGift(next); }}
                  interval={selectedCadence}
                  emailError={!!gift.email && !validateEmail(gift.email)}
                  keepPaying={buyerKeepsPaying}
                />
              )}

              {offersKeepPaying && (
                <GiftRenewalChoice
                  keepPaying={keepPaying}
                  onChange={setKeepPaying}
                  interval={selectedCadence}
                  productName={membershipProduct.name}
                  recipientName={gift.name}
                  priceLabel={renewalPrice}
                />
              )}

              {gift.enabled && notGiftable && (
                <Alert
                  color="warning"
                  role="alert"
                  description={<><strong>{membershipProduct.name} can't be bought as a gift.</strong>{' '}Pick another membership, or turn on gifting for it in the Membership creator.</>}
                />
              )}

              <div className="row" style={{ marginTop: "20px", marginBottom: "26px" }}>
                <div className="col-xs-auto" style={{ paddingLeft: "15px" }}>
                  <i className="fas fa-exclamation-circle small" style={{ color: "#f8cb58" }}></i>
                </div>
                <div className="col">
                  <p className="mb-0 small" style={{ color: "#686868" }}>
                    By clicking "Continue to Payment," I confirm that I have read and accept the{' '}
                    <a target="_blank" rel="noopener noreferrer" href="https://www.opendate.io/terms">Terms of Service</a>,{' '}
                    <a target="_blank" rel="noopener noreferrer" href="https://www.opendate.io/privacy">Privacy Policy</a>{' '}
                    and Refund Policy. I also agree that Opendate may share my information with the event organizer.
                  </p>
                </div>
              </div>

              <div className="row d-md-none">
                <div className="col" style={{ marginBottom: "15px" }}>
                  <OrderSummary />
                </div>
              </div>

              <div className="row" style={{ paddingTop: "5px" }}>
                <div className="col d-flex align-items-center">
                  <p className="mb-0" style={{ fontSize: "14px", color: "#686868" }}>
                    Powered by
                    <a href="https://www.opendate.io/" target="_blank" rel="noopener noreferrer">
                      <img src="/opendate_logo.png" className="ml-2" style={{ width: "88px" }} alt="Opendate" />
                    </a>
                  </p>
                </div>
                <div className="col text-right" style={{ alignItems: "center", display: "flex", justifyContent: "flex-end" }}>
                  <CancelLink cancelUrl={cancelUrl} disabled={isLoading} />
                  <Button variant="primary" type="submit" disabled={!canProceedToPayment()}>
                    {isLoading ? 'Loading...' : 'Continue to Payment'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3 order-first order-md-last">
            <SecondColumn title="Order Summary" />
          </div>
        </div>
      </div>
    );
  }

  if (step === 'payment' && membership) {
    if (timeLimitReached) {
      return (
        <div className="text-center" style={{ padding: "60px 15px" }}>
          <p className="mb-2" style={{ fontSize: "21px" }}>
            <strong>Time Limit Reached</strong>
          </p>
          <p className="mb-3 text-muted" style={{ fontSize: "14px" }}>
            Your reservation has been released. Please re-start your purchase.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              setTimeLimitReached(false);
              setCheckoutTimePercentageRemaining(100);
              setStep('customer');
              setMembership(null);
            }}
          >
            Start Over
          </Button>
        </div>
      );
    }

    return (
      <div style={holdHeight ? { minHeight: `${holdHeight}px` } : undefined}>
        <EventHeader confirm={confirm} />
        <div className="row">
          <div className="col">
            <p className="mb-3 mt-1 text-dark" style={{ fontSize: "24px" }}>
              <strong>Checkout</strong>
            </p>
          </div>
          <div className="col-xs-auto" style={{ paddingRight: "15px" }}>
            <div className="row mt-2">
              <div className="col small">
                <p className="mb-1">
                  <strong>
                    Time left{' '}
                    <Countdown
                      date={countdownRef.current.target}
                      onTick={() => {
                        const remainingTime = countdownRef.current.target - Date.now();
                        const percentage = (remainingTime / countdownRef.current.total) * 100;
                        setCheckoutTimePercentageRemaining(percentage);
                      }}
                      onComplete={() => {
                        setCheckoutTimePercentageRemaining(0);
                        setTimeLimitReached(true);
                      }}
                      renderer={countdownRenderer}
                    />
                  </strong>
                </p>
                <div className="progress" style={{
                  background: gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#f9d8ce" : "#d9edb0",
                  height: "8px"
                }}>
                  <div className="progress-bar animate-change-to-width" style={{
                    background: gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#ec4926" : "#98c648",
                    width: `${checkoutTimePercentageRemaining}%`,
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px"
                  }} />
                </div>
              </div>
              <div className="col-xs-auto" style={{ paddingRight: "15px" }}>
                <i className="fas fa-clock mt-1" style={{
                  fontSize: "24px",
                  color: gettingCloseToExpiration(checkoutTimePercentageRemaining) ? "#ec4926" : "#98c648"
                }} />
              </div>
            </div>
          </div>
        </div>
        <div className="row" style={{ marginTop: "13px" }}>
          <div className="col-12 col-md-6 col-lg-8 mb-3">
            <div className="row">
              <div className="col-12">
                <p style={{ fontSize: "16px", marginBottom: "12px" }}>
                  <strong>Contact Info</strong>
                </p>
              </div>
            </div>
            <ContactInfoForm
              firstName={customer.first_name}
              lastName={customer.last_name}
              email={customer.email}
              confirmEmail={customer.email}
              onFirstNameChange={() => {}}
              onLastNameChange={() => {}}
              onEmailChange={() => {}}
              onConfirmEmailChange={() => {}}
              disabled={true}
            />

            {membership.gift && (
              <p className="small" style={{ color: "#686868" }}>
                {giftSummary(membership.gift)}
              </p>
            )}

            {stripePromise && membership.stripe_client_secret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: membership.stripe_client_secret,
                  fonts: [{
                    cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans:400,600,700"
                  }],
                  appearance: stripeAppearance
                }}
              >
                <CheckoutFormInner
                  membership={membership}
                  baseUrl={baseUrl}
                  cancelUrl={cancelUrl}
                  submitLabel={membership.gift ? `Buy gift · ${formatter.format(total)}` : 'Place Order'}
                  onPaymentReady={() => setTimeout(() => setHoldHeight(null), 800)}
                />
              </Elements>
            )}
          </div>
          <div className="col-12 col-md-6 col-lg-4 mb-3 order-first order-md-last">
            <SecondColumn title="Order Summary" />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

MembershipCheckoutWidget.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  membershipProduct: PropTypes.object.isRequired,
  benefits: PropTypes.array,
  price: PropTypes.object,
  team: PropTypes.object.isRequired,
  venueOwnership: PropTypes.object.isRequired,
  stripePublishableKey: PropTypes.string.isRequired,
  stripeConnectedAccountId: PropTypes.string,
  baseUrl: PropTypes.string.isRequired,
  userContext: PropTypes.object,
  existingMembership: PropTypes.object,
  skipLanding: PropTypes.bool,
  postUrl: PropTypes.string,
  cancelUrl: PropTypes.string,
  confirm: PropTypes.object,
  giftingEnabled: PropTypes.bool
};

export default MembershipCheckoutWidget;
